import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { Bold, Heading2, Heading3, Italic, Link2, List, ListOrdered, Quote, Redo2, Undo2, Unlink } from "lucide-react";

const Tool = ({ onClick, active, label, children, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} aria-label={label} title={label} aria-pressed={!!active}
    className={`p-2 transition-colors ${active ? "bg-navy text-paper" : "text-navy hover:bg-mist"}`} data-testid={testId}>
    {children}
  </button>
);

export const RichEditor = ({ value, onChange, placeholder, testId }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, code: false, codeBlock: false, horizontalRule: false,
        link: { openOnClick: false, autolink: true, protocols: ["http", "https", "mailto"] } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === "" && editor.isEmpty)) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link address (starts with https://)", prev);
    if (url === null) return;
    if (!url.trim() || url.trim() === "https://") return c().unsetLink().run();
    c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const p = testId;
  return (
    <div className="rich-editor border border-navy/20 bg-white focus-within:border-navy" data-testid={testId}>
      <div className="flex flex-wrap gap-0.5 border-b border-navy/10 p-1" role="toolbar" aria-label="Text formatting">
        <Tool label="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()} testId={`${p}-bold`}><Bold size={16} /></Tool>
        <Tool label="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()} testId={`${p}-italic`}><Italic size={16} /></Tool>
        <Tool label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} testId={`${p}-h2`}><Heading2 size={16} /></Tool>
        <Tool label="Small heading" active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} testId={`${p}-h3`}><Heading3 size={16} /></Tool>
        <Tool label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} testId={`${p}-bullets`}><List size={16} /></Tool>
        <Tool label="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()} testId={`${p}-numbers`}><ListOrdered size={16} /></Tool>
        <Tool label="Quote" active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()} testId={`${p}-quote`}><Quote size={16} /></Tool>
        <Tool label="Add link" active={editor.isActive("link")} onClick={setLink} testId={`${p}-link`}><Link2 size={16} /></Tool>
        {editor.isActive("link") && <Tool label="Remove link" onClick={() => c().unsetLink().run()} testId={`${p}-unlink`}><Unlink size={16} /></Tool>}
        <span className="mx-1 w-px bg-navy/10" aria-hidden="true" />
        <Tool label="Undo" onClick={() => c().undo().run()} testId={`${p}-undo`}><Undo2 size={16} /></Tool>
        <Tool label="Redo" onClick={() => c().redo().run()} testId={`${p}-redo`}><Redo2 size={16} /></Tool>
      </div>
      <EditorContent editor={editor} className="article-body" data-testid={`${p}-content`} />
    </div>
  );
};
